/**
 * MVSETUP runner (Faz 10.4 — OCS mvsetup.rs parity, docs/opencadstudio-parity/
 * 10-workflow-performance.md §3.3).
 *
 * OCS truth (§2.3): model space asks "Enable paper space?" — yes switches to
 * a layout (yap chains an MVIEW-style placement); no builds the setup in the
 * model: scale + paper W/H + a CLOSED border polyline from (0,0) to
 * (W·scale, H·scale) + zoom-to-fit. In a layout the verb set is
 * Create / Delete objects / Scale viewports. Create arrangements:
 * Single, Engineering (2×2 Top/ISO/Front/Right) and Array (columns, rows,
 * gapX, gapY — 1..100) over a two-corner area whose default is the whole
 * sheet; a degenerate cell below the 1e-6 threshold reports "viewports do
 * not fit". Scale viewports multiplies every viewport's paper-per-model
 * scale by the paper/model ratio entered.
 *
 * Unit note (yap difference): yap paper sheets live in meters (PAPER_SIZES),
 * but the MVSETUP border is a MODEL-space drawing frame — the paper W/H
 * prompt takes plain paper units (mm for the A-series defaults) and the
 * border spans W·scale × H·scale model units, exactly the OCS semantics.
 */

import { runInteraction, PN, PT } from './lib';
import { valueAt, requireSelection } from './lib';
import type { RunnerMap } from '../runnerRegistry';
import { useCommandInteract } from '../commandInteract';
import { useDynamicInputStore } from '../../../state/useDynamicInputStore';
import { useLayoutStore, type CadLayout, type CadViewport } from '../../../state/useLayoutStore';
import { undoableAdd, undoableDelete, polylineEntity } from '../entityOps';

/** Smallest viewport cell that still counts as "fits" (OCS fit threshold). */
const FIT_EPSILON = 1e-6;

/** Emit a runner message from OUTSIDE a completing interaction (the visible
 *  command-line log path — finishCurrent appends the same way). */
function reportLine(message: string): void {
    useDynamicInputStore.getState().appendLog([message, '']);
}

interface Rect { x: number; y: number; w: number; h: number }

/** Normalize two picked corners into a positive rect. */
function rectFromCorners(a: { x: number; y: number }, b: { x: number; y: number }): Rect {
    return {
        x: Math.min(a.x, b.x),
        y: Math.min(a.y, b.y),
        w: Math.abs(b.x - a.x),
        h: Math.abs(b.y - a.y),
    };
}

function viewportOf(layout: CadLayout, r: Rect, title: string): Omit<CadViewport, 'id'> {
    return {
        cx: r.x + r.w / 2,
        cy: r.y + r.h / 2,
        w: r.w,
        h: r.h,
        viewCx: 0,
        viewCy: 0,
        scale: 0.02,
        frozenLayers: [],
        showTitle: true,
        title,
    };
}

/** Create the arranged viewports of one layout and report. */
function commitArrangement(layoutId: string, kind: 'single' | 'engineering' | 'array', area: Rect, array?: { cols: number; rows: number; gapX: number; gapY: number }): string {
    const store = useLayoutStore.getState();
    const layout = store.layouts.find((l) => l.id === layoutId);
    if (!layout) return 'MVSETUP: layout no longer exists.';

    const cells: { r: Rect; title: string }[] = [];
    if (kind === 'single') {
        cells.push({ r: area, title: 'Plan' });
    } else if (kind === 'engineering') {
        // OCS Engineering preset: 2×2 — Top / ISO above, Front / Right below.
        const cw = area.w / 2, ch = area.h / 2;
        cells.push({ r: { x: area.x, y: area.y + ch, w: cw, h: ch }, title: 'Top' });
        cells.push({ r: { x: area.x + cw, y: area.y + ch, w: cw, h: ch }, title: 'ISO' });
        cells.push({ r: { x: area.x, y: area.y, w: cw, h: ch }, title: 'Front' });
        cells.push({ r: { x: area.x + cw, y: area.y, w: cw, h: ch }, title: 'Right' });
    } else {
        const { cols, rows, gapX, gapY } = array!;
        const cw = (area.w - (cols - 1) * gapX) / cols;
        const ch = (area.h - (rows - 1) * gapY) / rows;
        if (cw <= FIT_EPSILON || ch <= FIT_EPSILON) return 'MVSETUP: viewports do not fit in the area (columns/rows/gaps too large).';
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                cells.push({
                    r: {
                        x: area.x + col * (cw + gapX),
                        y: area.y + row * (ch + gapY),
                        w: cw,
                        h: ch,
                    },
                    title: `VP ${row + 1}.${col + 1}`,
                });
            }
        }
    }
    if (cells.some((c) => c.r.w <= FIT_EPSILON || c.r.h <= FIT_EPSILON)) {
        return 'MVSETUP: viewports do not fit in the area.';
    }
    cells.forEach((c) => store.addViewport(layoutId, viewportOf(layout, c.r, c.title)));
    window.dispatchEvent(new CustomEvent('yap:zoom-to-fit'));
    return `MVSETUP: ${cells.length} viewport(s) created on "${layout.name}" at 1:50 (Scale viewports adjusts).`;
}

/** Shared two-corner area prompt; Enter without corners = the whole sheet
 *  (OCS "default paper area"). Runs after the arrangement is known. */
function startAreaPick(layoutId: string, kind: 'single' | 'engineering' | 'array', array?: { cols: number; rows: number; gapX: number; gapY: number }): void {
    runInteraction({
        steps: [
            PT('MVSETUP: first corner of the viewport area (Enter = entire sheet)'),
            PT('MVSETUP: opposite corner'),
        ],
        complete: (pts) => {
            if (pts.length >= 2) {
                return commitArrangement(layoutId, kind, rectFromCorners(pts[0], pts[1]), array);
            }
            const layout = useLayoutStore.getState().layouts.find((l) => l.id === layoutId);
            if (!layout) return 'MVSETUP: layout no longer exists.';
            return commitArrangement(layoutId, kind, { x: 0, y: 0, w: layout.width, h: layout.height }, array);
        },
    });
}

/** Layout branch: Create / Delete objects / Scale viewports (OCS verbs). */
function startLayoutMvsetup(layoutId: string): void {
    runInteraction({
        steps: [
            {
                prompt: 'MVSETUP: enter an option [Create/Delete objects/Scale viewports]:',
                kind: 'point',
                options: [{ keyword: 'Create' }, { keyword: 'Delete objects', label: 'Delete' }, { keyword: 'Scale viewports', label: 'Scale' }],
            },
        ],
        onOption: (keyword) => {
            const k = keyword.toLowerCase();
            useCommandInteract.getState().cancelActive();
            if (k === 'CREATE') {
                startCreateBranch(layoutId);
            } else if (k === 'DELETE OBJECTS') {
                const sel = requireSelection('MVSETUP Delete');
                if (sel && sel.length > 0) {
                    undoableDelete(sel.map((e) => e.id), 'MVSETUP: delete objects');
                    reportLine(`MVSETUP: ${sel.length} object(s) deleted.`);
                }
            } else {
                startScaleBranch(layoutId);
            }
        },
        complete: () => 'MVSETUP: pick Create, Delete objects or Scale viewports.',
    });
}

/** Create → arrangement chips → (array metrics) → area corners. */
function startCreateBranch(layoutId: string): void {
    runInteraction({
        steps: [
            {
                prompt: 'MVSETUP Create: enter arrangement [Single/Engineering/Array]:',
                kind: 'point',
                options: [{ keyword: 'Single' }, { keyword: 'Engineering' }, { keyword: 'Array' }],
            },
        ],
        onOption: (keyword) => {
            const k = keyword.toLowerCase();
            useCommandInteract.getState().cancelActive();
            if (k === 'ARRAY') {
                startArrayMetrics(layoutId);
            } else {
                startAreaPick(layoutId, k === 'ENGINEERING' ? 'engineering' : 'single');
            }
        },
        complete: () => 'MVSETUP Create: pick Single, Engineering or Array.',
    });
}

/** OCS Array metrics: columns <2>, rows <2>, gapX <0>, gapY <0> (1..100). */
function startArrayMetrics(layoutId: string): void {
    runInteraction({
        steps: [
            PN('MVSETUP Array: number of columns <2>', 2),
            PN('MVSETUP Array: number of rows <2>', 2),
            PN('MVSETUP Array: gap between columns <0>', 0),
            PN('MVSETUP Array: gap between rows <0>', 0),
        ],
        complete: (_p, values) => {
            const cols = Math.min(100, Math.max(1, Math.round(valueAt(values, 0, 2))));
            const rows = Math.min(100, Math.max(1, Math.round(valueAt(values, 1, 2))));
            const gapX = Math.max(0, valueAt(values, 2, 0));
            const gapY = Math.max(0, valueAt(values, 3, 0));
            startAreaPick(layoutId, 'array', { cols, rows, gapX, gapY });
            return `MVSETUP Array: ${cols}×${rows} cells, gaps ${gapX}/${gapY} — pick the area.`;
        },
    });
}

/** Scale viewports: paper/model ratio multiplies every viewport scale. */
function startScaleBranch(layoutId: string): void {
    runInteraction({
        steps: [
            PN('MVSETUP Scale: paper units <1>', 1),
            PN('MVSETUP Scale: model units <1>', 1),
        ],
        complete: (_p, values) => {
            const paper = valueAt(values, 0, 1);
            const model = valueAt(values, 1, 1);
            if (paper <= 0 || model <= 0) return 'MVSETUP Scale: the paper/model ratio must be positive.';
            const store = useLayoutStore.getState();
            const layout = store.layouts.find((l) => l.id === layoutId);
            if (!layout) return 'MVSETUP: layout no longer exists.';
            if (layout.viewports.length === 0) return 'MVSETUP Scale: no viewports on this layout (Create first).';
            const mult = paper / model;
            layout.viewports.forEach((vp) => store.updateViewport(layoutId, vp.id, { scale: vp.scale * mult }));
            const newScale = layout.viewports[0].scale * mult;
            return `MVSETUP: ${layout.viewports.length} viewport(s) scaled ×${paper}/${model} — now 1:${Math.round(1 / newScale)}.`;
        },
    });
}

/** Model-space "No" branch: scale + paper W/H + closed border polyline. */
function startModelBorder(): void {
    runInteraction({
        steps: [
            PN('MVSETUP: model units per paper unit (scale) <1>', 1),
            PN('MVSETUP: paper width (paper units, A3 = 420) <420>', 420),
            PN('MVSETUP: paper height (paper units, A3 = 297) <297>', 297),
        ],
        complete: (_p, values) => {
            const scale = Math.max(FIT_EPSILON, valueAt(values, 0, 1));
            const w = Math.max(1, valueAt(values, 1, 420)) * scale;
            const h = Math.max(1, valueAt(values, 2, 297)) * scale;
            const border = polylineEntity(
                [[0, 0, 0], [w, 0, 0], [w, h, 0], [0, h, 0], [0, 0, 0]],
                { name: `MVSETUP border`, closed: true }
            );
            undoableAdd([border], 'MVSETUP: border');
            window.dispatchEvent(new CustomEvent('yap:zoom-to-fit'));
            return `MVSETUP: border placed at (0,0) spanning ${w.toFixed(2)} × ${h.toFixed(2)} model units (scale ${scale}).`;
        },
    });
}

/** Model-space "Yes" branch: create a layout, switch, chain MVIEW placement. */
function startModelToPaper(): void {
    const store = useLayoutStore.getState();
    const layout = store.addLayout();
    store.setActive(layout.id);
    window.dispatchEvent(new CustomEvent('yap:zoom-to-fit'));
    startAreaPick(layout.id, 'single');
    reportLine(`MVSETUP: switched to paper space ("${layout.name}") — place the viewport.`);
}

export const mvsetupRunners: RunnerMap = {
    'file.mvsetup': () => {
        const activeId = useLayoutStore.getState().activeId;
        if (activeId) {
            startLayoutMvsetup(activeId);
            return 'MVSETUP: choose an action (layout mode).';
        }
        runInteraction({
            steps: [
                {
                    prompt: 'MVSETUP: enable paper space? [Yes/No]:',
                    kind: 'point',
                    options: [{ keyword: 'Yes' }, { keyword: 'No' }],
                },
            ],
            onOption: (keyword) => {
                useCommandInteract.getState().cancelActive();
                if (keyword.toUpperCase() === 'YES') startModelToPaper();
                else startModelBorder();
            },
            complete: () => 'MVSETUP: pick Yes (paper space) or No (model border).',
        });
        return 'MVSETUP: enable paper space? [Yes/No]:';
    },
};
